/**
 * Stamp request service.
 * Reads via Supabase RLS-gated SELECT; writes via /api/stamp/* (service-role).
 */
import type { TransactionBlock } from '@mysten/sui.js/transactions';
import { supabase } from '../lib/supabase';
import { apiFetch } from './api-client';
import { normalizeAddress } from '../lib/shop-address';
import { STAMP_AUTHORITY_ID } from './contract-constants';
import {
  buildRecordPurchaseTx, buildBatchRecordPurchaseTx, buildRecordPurchaseAttestedTx,
} from './sui-service';

const STAMP_COOLDOWN_MS = 300_000; // 5 minutes
const COOLDOWN_STORAGE_KEY = 'coffee_rewards_last_stamp';

export interface StampRequestRow {
  id: string;
  customer_address: string;
  customer_name: string | null;
  shop_id: string;
  shop_owner_address: string;
  customer_shop_account_id: string | null;
  customer_profile_id: string | null;
  quantity: number;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'stuck';
  onchain_tx_hash: string | null;
  onchain_status: 'none' | 'submitting' | 'confirmed' | 'failed';
  created_at: string;
  responded_at: string | null;
  confirmed_at: string | null;
  expires_at: string;
  // Geo signal (backlog #16) — null = unknown (no permission, no shop coords).
  geo_bucket: 'at_counter' | 'far' | null;
  geo_distance_m: number | null;
  // Server attestation (contracts/STAMP_AUTHORIZATION_DESIGN.md) — null on
  // every row until the rollout is complete (key unset, chain read failed, or
  // a legacy row from before this shipped). buildStampOnChainTx below is the
  // only place these three should be read.
  attestation_sig: string | null;
  attestation_seq: number | null;
  attestation_expires_at: string | null;
}

// A buffered Square payment a customer tap could claim (manual-fallback option).
export interface SquareClaimablePayment {
  id: string;
  amount_cents: number | null;
  currency: string;
  terminal_id: string | null;
  paid_at: string | null;
}

// createStampRequest may return extra Square hints alongside the stamp row:
//  - squareMatched: auto-approved by a Square payment → go straight to signing
//  - squareCandidates: ambiguous → show "which was yours?" then claimSquarePayment
//  - squarePending: tap-then-pay → wait (webhook will approve), same as barista wait
export type CreateStampResult = StampRequestRow & {
  source?: 'barista' | 'square';
  squareMatched?: boolean;
  squarePending?: boolean;
  squareCandidates?: SquareClaimablePayment[];
};

export function getStampCooldownRemaining(customerAddress: string, shopOwnerAddress: string): number {
  try {
    const stored = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (!stored) return 0;
    const cooldowns: Record<string, number> = JSON.parse(stored);
    const key = `${customerAddress}_${shopOwnerAddress}`;
    const lastStamp = cooldowns[key] || 0;
    return Math.max(0, STAMP_COOLDOWN_MS - (Date.now() - lastStamp));
  } catch {
    return 0;
  }
}

export function recordStampCooldown(customerAddress: string, shopOwnerAddress: string): void {
  try {
    const stored = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    const cooldowns: Record<string, number> = stored ? JSON.parse(stored) : {};
    cooldowns[`${customerAddress}_${shopOwnerAddress}`] = Date.now();
    localStorage.setItem(COOLDOWN_STORAGE_KEY, JSON.stringify(cooldowns));
  } catch { /* ignore */ }
}

// Geo signal (backlog #16). SIGNAL only — never gates the request and NEVER
// prompts for permission. Only reads a position when the browser has
// ALREADY granted geolocation (checked via the Permissions API first); any
// absence of that API, any non-'granted' state, or any failure/timeout means
// the request goes out with no coords, exactly as it did before this feature.
const GEO_TIMEOUT_MS = 3000;
const GEO_MAX_AGE_MS = 60_000;

async function getGrantedGeoCoords(): Promise<{ lat: number; lng: number } | null> {
  try {
    if (!navigator.permissions?.query || !navigator.geolocation) return null;
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    if (status.state !== 'granted') return null;

    const position = new Promise<{ lat: number; lng: number } | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: GEO_TIMEOUT_MS, maximumAge: GEO_MAX_AGE_MS },
      );
    });
    // Defensive: some browsers don't reliably honor the native `timeout`
    // option once permission is already granted. Race it so the stamp
    // request is never held up by geolocation, no matter what the browser does.
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), GEO_TIMEOUT_MS));
    return await Promise.race([position, timeout]);
  } catch {
    return null;
  }
}

export async function createStampRequest(params: {
  customerAddress: string;
  shopId: string;
  shopOwnerAddress: string;
  customerShopAccountId: string;
  customerProfileId: string;
  customerName?: string;
  quantity?: number;
}): Promise<CreateStampResult | null> {
  try {
    const coords = await getGrantedGeoCoords();
    return await apiFetch<CreateStampResult>('/api/stamp', {
      action: 'create',
      shopId: params.shopId,
      shopOwnerAddress: params.shopOwnerAddress,
      customerShopAccountId: params.customerShopAccountId,
      customerProfileId: params.customerProfileId,
      customerName: params.customerName,
      quantity: params.quantity,
      ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
    });
  } catch (err: any) {
    console.error('createStampRequest failed:', err?.message || err);
    return null;
  }
}

// Customer picks which buffered Square payment was theirs (manual fallback);
// the chosen payment is claimed and the stamp is approved → proceed to signing.
export async function claimSquarePayment(
  requestId: string,
  paymentId: string,
): Promise<StampRequestRow | null> {
  try {
    return await apiFetch<StampRequestRow>('/api/stamp', {
      action: 'square-claim', requestId, paymentId,
    });
  } catch (err: any) {
    console.error('claimSquarePayment failed:', err?.message || err);
    return null;
  }
}

export interface ApproveStampError {
  status: number;
  code: string;
}

export async function approveStampRequest(
  requestId: string,
  quantity: number = 1,
  compCode?: string,
): Promise<StampRequestRow | null> {
  const body: Record<string, unknown> = {
    action: 'respond', requestId, decision: 'approved', quantity,
  };
  if (compCode) body.comp_code = compCode;
  try {
    return await apiFetch<StampRequestRow>('/api/stamp', body);
  } catch (err: any) {
    // Re-throw as a typed error if the comp redemption failed, so the caller
    // (barista UI) can show a friendly per-reason message. For non-comp paths,
    // preserve the existing "log + return null" contract.
    if (compCode && err?.status) {
      const error: ApproveStampError = { status: err.status, code: err.message || 'unknown' };
      throw error;
    }
    console.error('approveStampRequest failed:', err?.message || err);
    return null;
  }
}

export async function denyStampRequest(requestId: string): Promise<StampRequestRow | null> {
  try {
    return await apiFetch<StampRequestRow>('/api/stamp', {
      action: 'respond', requestId, decision: 'denied',
    });
  } catch (err: any) {
    console.error('denyStampRequest failed:', err?.message || err);
    return null;
  }
}

/**
 * Choose the on-chain tx builder for an approved stamp
 * (contracts/STAMP_AUTHORIZATION_DESIGN.md). Prefers the attested path when
 * BOTH the server issued an attestation on this row AND this network's
 * StampAuthority object id is configured (src/config/sui-networks.ts —
 * stampAuthorityId stays '' until the admin creates the authority on-chain).
 * Falls back to the legacy unattested path (single or batch record_purchase)
 * whenever either is missing OR the attestation has expired (see
 * attestationUsable) — obvious and commented rather than silent,
 * because this fallback is load-bearing during the rollout: the key can be
 * unset, the chain read can fail, or the authority may not exist yet, and
 * none of those should ever block a stamp from recording on-chain.
 */
/**
 * Sponsorship round-trip + ephemeral signing costs a few seconds, so an
 * attestation that is merely "not yet expired" can still expire in flight.
 */
export const ATTESTATION_SUBMIT_MARGIN_MS = 30_000;

/**
 * Is this row's attestation still good enough to submit?
 *
 * Presence of a signature is NOT enough. Attestations carry a 10-minute TTL
 * (ATTESTATION_TTL_MS), and the stuck-stamp recovery replays rows that are
 * hours old — record_purchase_attested rejects an expired expiresMs, and
 * Shinami refuses to sponsor a transaction that would abort, so such a row can
 * never recover and simply retries the same dead transaction forever.
 *
 * Pure, and takes `nowMs`, so it is testable on its own: tests run against
 * testnet where stampAuthorityId is '', which makes the attested branch of
 * buildStampOnChainTx unreachable and a test through it worthless.
 */
export function attestationUsable<
  T extends Pick<StampRequestRow, 'attestation_sig' | 'attestation_expires_at'>,
>(
  row: T,
  nowMs: number = Date.now(),
): row is T & { attestation_sig: string; attestation_expires_at: string } {
  if (!row.attestation_sig || !row.attestation_expires_at) return false;
  const expiresMs = new Date(row.attestation_expires_at).getTime();
  if (!Number.isFinite(expiresMs)) return false;
  return expiresMs - ATTESTATION_SUBMIT_MARGIN_MS > nowMs;
}

export function buildStampOnChainTx(
  row: Pick<StampRequestRow, 'attestation_sig' | 'attestation_expires_at'>,
  accountObjectId: string,
  profileObjectId: string,
  shopObjectId: string,
  quantity: number,
): TransactionBlock {
  if (attestationUsable(row) && STAMP_AUTHORITY_ID) {
    const expiresMs = new Date(row.attestation_expires_at).getTime();
    return buildRecordPurchaseAttestedTx(
      accountObjectId, profileObjectId, shopObjectId,
      row.attestation_sig, quantity, expiresMs,
    );
  }
  // ── Fallback: legacy unattested path ──────────────────────────────────
  return quantity > 1
    ? buildBatchRecordPurchaseTx(accountObjectId, profileObjectId, shopObjectId, quantity)
    : buildRecordPurchaseTx(accountObjectId, profileObjectId, shopObjectId);
}

export interface StoredAttestation {
  attestation_sig: string;
  attestation_seq: number;
  attestation_expires_at: string;
  quantity: number;
}

// Retry path after an app reload dropped the attestation that came back with
// the original approval response — re-fetches whatever the server already
// persisted (never re-signs; api/stamp.ts's stamp-attestation action is a
// pure read of the row).
export async function fetchStampAttestation(requestId: string): Promise<StoredAttestation | null> {
  try {
    return await apiFetch<StoredAttestation>('/api/stamp', { action: 'stamp-attestation', requestId });
  } catch (err: any) {
    console.error('fetchStampAttestation failed:', err?.message || err);
    return null;
  }
}

export async function updateStampOnchainStatus(
  requestId: string,
  onchainStatus: 'submitting' | 'confirmed' | 'failed',
  txHash?: string,
): Promise<void> {
  try {
    await apiFetch('/api/stamp', { action: 'onchain-status', requestId, onchainStatus, txHash });
  } catch (err: any) {
    console.error('updateStampOnchainStatus failed:', err?.message || err);
  }
}

// Reads stay on direct Supabase client (RLS gates SELECT).
export async function getPendingStampsForShop(shopOwnerAddress: string): Promise<StampRequestRow[]> {
  const { data, error } = await supabase
    .from('stamp_requests')
    .select('*')
    .eq('shop_owner_address', normalizeAddress(shopOwnerAddress))
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data;
}

export async function getStuckStamps(customerAddress: string): Promise<StampRequestRow[]> {
  // Approved stamps that haven't been confirmed on-chain yet — includes
  // 'none' (customer missed the realtime approval event), 'submitting' (tx
  // started but didn't confirm), and 'failed' (retry candidates).
  const { data, error } = await supabase
    .from('stamp_requests')
    .select('*')
    .eq('customer_address', customerAddress)
    .eq('status', 'approved')
    .in('onchain_status', ['none', 'submitting', 'failed'])
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data;
}
